import { CSSObject } from '@mui/material';

import { ChipSize, ChipType, ChipVariant } from './chip.types';
import { COLOR_TOKENS } from '../colors/colors';
import { RADIUS, SPACING } from '../spacing/spacing';
import { TYPOGRAPHY_VARIANTS } from '../typography/typography';

const COMMON_CHIP_STYLES: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: `${COLOR_TOKENS['Colors/Base/Transparent']}`,
  border: `1px solid ${COLOR_TOKENS['Colors/Base/Black']}`,
  height: 'unset',
};

const SMALL_TEXT_STYLES: CSSObject = {
  '& .MuiChip-label': {
    ...TYPOGRAPHY_VARIANTS['text-xs-medium'],
    userSelect: 'none',
    paddingLeft: SPACING['spacing-none'],
    paddingRight: SPACING['spacing-none'],
    paddingTop: SPACING['spacing-none'],
    paddingBottom: SPACING['spacing-none'],
  },
};

const MEDIUM_LARGE_TEXT_STYLES: CSSObject = {
  '& .MuiChip-label': {
    ...TYPOGRAPHY_VARIANTS['text-sm-medium'],
    userSelect: 'none',
    paddingLeft: SPACING['spacing-none'],
    paddingRight: SPACING['spacing-none'],
    paddingTop: SPACING['spacing-none'],
    paddingBottom: SPACING['spacing-none'],
  },
};

export const CHIP_STYLES: Record<ChipSize, Record<ChipType, Record<ChipVariant, CSSObject>>> = {
  sm: {
    default: {
      badge: {
        borderRadius: RADIUS['radius-md'],
        ...COMMON_CHIP_STYLES,
        padding: `${SPACING['spacing-xxs']} ${SPACING['spacing-md']}`,
        ...SMALL_TEXT_STYLES,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        padding: `${SPACING['spacing-xxs']} ${SPACING['spacing-md']}`,
        ...SMALL_TEXT_STYLES,
      },
      tag: {
        borderRadius: RADIUS['radius-sm'],
        ...COMMON_CHIP_STYLES,
        ...SMALL_TEXT_STYLES,
        gap: 3,
        paddingLeft: SPACING['spacing-md'],
        paddingRight: SPACING['spacing-md'],
        paddingTop: 3,
        paddingBottom: 3,
      },
    },
    leading: {
      badge: {
        borderRadius: RADIUS['radius-md'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: SPACING['spacing-xs'],
        paddingRight: SPACING['spacing-sm'],
        paddingTop: SPACING['spacing-xxs'],
        paddingBottom: SPACING['spacing-xxs'],
        gap: SPACING['spacing-xs'],
        ...SMALL_TEXT_STYLES,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: 6,
        paddingRight: SPACING['spacing-md'],
        paddingTop: SPACING['spacing-xxs'],
        paddingBottom: SPACING['spacing-xxs'],
        gap: SPACING['spacing-xs'],
        ...SMALL_TEXT_STYLES,
      },
      tag: {
        borderRadius: RADIUS['radius-sm'],
        ...COMMON_CHIP_STYLES,
        ...SMALL_TEXT_STYLES,
        gap: 3,
        paddingLeft: 5,
        paddingRight: SPACING['spacing-md'],
        paddingTop: 3,
        paddingBottom: 3,
      },
    },
    trailing: {
      badge: {
        borderRadius: RADIUS['radius-md'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: SPACING['spacing-md'],
        paddingRight: 3,
        paddingTop: SPACING['spacing-xxs'],
        paddingBottom: SPACING['spacing-xxs'],
        gap: SPACING['spacing-xs'],
        ...SMALL_TEXT_STYLES,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: SPACING['spacing-md'],
        paddingRight: 3,
        paddingTop: SPACING['spacing-xxs'],
        paddingBottom: SPACING['spacing-xxs'],
        gap: SPACING['spacing-xs'],
        ...SMALL_TEXT_STYLES,
      },
      tag: {
        borderRadius: RADIUS['radius-sm'],
        ...COMMON_CHIP_STYLES,
        ...SMALL_TEXT_STYLES,
        gap: 3,
        paddingRight: 5,
        paddingLeft: SPACING['spacing-md'],
        paddingTop: 3,
        paddingBottom: 3,
      },
    },
    only: {
      badge: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        padding: 5,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        padding: 5,
      },
      tag: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        padding: 5,
      },
    },
  },
  md: {
    default: {
      badge: {
        borderRadius: RADIUS['radius-md'],
        ...COMMON_CHIP_STYLES,
        ...TYPOGRAPHY_VARIANTS['text-xs-medium'],
        padding: `${SPACING['spacing-xxs']} 10`,
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        ...TYPOGRAPHY_VARIANTS['text-xs-medium'],
        padding: `${SPACING['spacing-xxs']} 10`,
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      tag: {
        ...COMMON_CHIP_STYLES,
        ...MEDIUM_LARGE_TEXT_STYLES,
        borderRadius: RADIUS['radius-sm'],
        gap: 3,
        paddingRight: 9,
        paddingLeft: 9,
        paddingTop: 2,
        paddingBottom: 3,
      },
    },
    leading: {
      badge: {
        borderRadius: RADIUS['radius-md'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: SPACING['spacing-md'],
        paddingRight: 10,
        paddingTop: SPACING['spacing-xxs'],
        paddingBottom: SPACING['spacing-xxs'],
        gap: SPACING['spacing-xs'],
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: SPACING['spacing-md'],
        paddingRight: 10,
        paddingTop: SPACING['spacing-xxs'],
        paddingBottom: SPACING['spacing-xxs'],
        gap: SPACING['spacing-xs'],
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      tag: {
        ...COMMON_CHIP_STYLES,
        ...MEDIUM_LARGE_TEXT_STYLES,
        borderRadius: RADIUS['radius-sm'],
        gap: 3,
        paddingLeft: SPACING['spacing-xs'],
        paddingRight: 9,
        paddingTop: 2,
        paddingBottom: 3,
      },
    },
    trailing: {
      badge: {
        ...COMMON_CHIP_STYLES,
        borderRadius: RADIUS['radius-md'],
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: SPACING['spacing-md'],
        paddingBottom: SPACING['spacing-md'],
        gap: SPACING['spacing-xs'],
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      pill: {
        ...COMMON_CHIP_STYLES,
        borderRadius: RADIUS['radius-full'],
        paddingLeft: 10,
        paddingRight: 8,
        paddingTop: SPACING['spacing-xxs'],
        paddingBottom: SPACING['spacing-xxs'],
        gap: SPACING['spacing-xs'],
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      tag: {
        ...COMMON_CHIP_STYLES,
        ...MEDIUM_LARGE_TEXT_STYLES,
        borderRadius: RADIUS['radius-sm'],
        gap: 3,
        paddingRight: SPACING['spacing-xs'],
        paddingLeft: 9,
        paddingTop: 2,
        paddingBottom: 3,
      },
    },
    only: {
      badge: {
        ...COMMON_CHIP_STYLES,
        borderRadius: RADIUS['radius-full'],
        padding: 6,
      },
      pill: {
        ...COMMON_CHIP_STYLES,
        borderRadius: RADIUS['radius-full'],
        padding: 6,
      },
      tag: {
        ...COMMON_CHIP_STYLES,
        borderRadius: RADIUS['radius-full'],
        padding: 6,
      },
    },
  },
  lg: {
    default: {
      badge: {
        borderRadius: RADIUS['radius-md'],
        ...COMMON_CHIP_STYLES,
        padding: `${SPACING['spacing-xs']} ${SPACING['spacing-lg']}`,
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        padding: `${SPACING['spacing-xs']} ${SPACING['spacing-lg']}`,
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      tag: {
        ...COMMON_CHIP_STYLES,
        ...MEDIUM_LARGE_TEXT_STYLES,
        borderRadius: RADIUS['radius-sm'],
        gap: 3,
        paddingRight: 10,
        paddingLeft: 10,
        paddingTop: SPACING['spacing-xs'],
        paddingBottom: SPACING['spacing-xs'],
      },
    },
    leading: {
      badge: {
        borderRadius: RADIUS['radius-md'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: 10,
        paddingRight: SPACING['spacing-lg'],
        paddingTop: SPACING['spacing-xs'],
        paddingBottom: SPACING['spacing-xs'],
        gap: 6,
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: 10,
        paddingRight: SPACING['spacing-lg'],
        paddingTop: SPACING['spacing-xs'],
        paddingBottom: SPACING['spacing-xs'],
        gap: 6,
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      tag: {
        ...COMMON_CHIP_STYLES,
        ...MEDIUM_LARGE_TEXT_STYLES,
        borderRadius: RADIUS['radius-sm'],
        gap: 3,
        paddingLeft: SPACING['spacing-xs'],
        paddingRight: 10,
        paddingTop: SPACING['spacing-xs'],
        paddingBottom: SPACING['spacing-xs'],
      },
    },
    trailing: {
      badge: {
        borderRadius: RADIUS['radius-md'],
        ...COMMON_CHIP_STYLES,
        paddingLeft: 10,
        paddingRight: SPACING['spacing-lg'],
        paddingTop: SPACING['spacing-xs'],
        paddingBottom: SPACING['spacing-xs'],
        gap: 6,
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        paddingRight: 10,
        paddingLeft: SPACING['spacing-lg'],
        paddingTop: SPACING['spacing-xs'],
        paddingBottom: SPACING['spacing-xs'],
        gap: 6,
        ...MEDIUM_LARGE_TEXT_STYLES,
      },
      tag: {
        ...COMMON_CHIP_STYLES,
        ...MEDIUM_LARGE_TEXT_STYLES,
        borderRadius: RADIUS['radius-sm'],
        gap: 3,
        paddingRight: 10,
        paddingLeft: SPACING['spacing-xs'],
        paddingTop: SPACING['spacing-xs'],
        paddingBottom: SPACING['spacing-xs'],
      },
    },
    only: {
      badge: {
        ...COMMON_CHIP_STYLES,
        borderRadius: RADIUS['radius-full'],
        padding: SPACING['spacing-md'],
      },
      pill: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        padding: SPACING['spacing-md'],
      },
      tag: {
        borderRadius: RADIUS['radius-full'],
        ...COMMON_CHIP_STYLES,
        padding: SPACING['spacing-md'],
      },
    },
  },
};
